import React from "react";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import DateFormatter from "@/main/common/DateFormatter";
import { Localizer } from "@/main/react/Types";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import QuestionsFilterChip from "@/main/react/component/QuestionsFilterChip";
import GroupButtons from "@/main/react/group/GroupButtons";
import { getGroupActivityItems, type GroupActivityItem } from "@/main/react/group/GroupActivityLine";
import { readMemberTotal, type GroupActivityFields } from "@/main/common/GroupActivityTypes";
import { getDisplayUsername, getUserProfilePath, hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import {
    GroupDiscussionOrderingStyle,
    GroupLeaderIdentity,
    GroupPageSelectOption,
    getGroupLeadership,
} from "./GroupPageShared";
import { ArrowLeft, ChevronLeft, ChevronRight, MessagesSquare, ShieldCheck } from "lucide-react";
import { prefersReducedMotion } from "@/main/util/motionPreference";
import { ProfileBanner } from "@/main/react/component/ProfileBanner";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { getFriendlyCountBadgeLabel, getFriendlyPrivateBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { useThumbnailLightbox } from "@/main/react/media/useThumbnailLightbox";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { GroupDiscussionSubmissionPolicyValue } from "@/main/react/group/GroupDiscussionSubmissionPolicy";
import type { GroupLeadershipLevel } from "@/main/common/GroupGovernanceApi";
import type { GroupWelcomeContent } from "@/main/react/group/GroupWelcomeDialog";
import { isBrowserOwnedClick } from "@/main/navigation/NavigationPolicy";
import { MetaHandler } from "@/main/common/MetaHandler";
import { getSensitivePreferenceMode, shouldMaskSensitiveMedia } from "@/main/common/SensitivePreference";

type BaseHeaderProps = {
    name?: string;
    description?: string | null;
    thumbnailUrl?: string | null;
    bannerUrl?: string | null;
    mainLocale?: string | null;
    discussionSubmissionPolicy?: GroupDiscussionSubmissionPolicyValue | null;
    creatorUsername?: string | null;
    creatorProfileImageUrl?: string | null;
    createdAt?: string | null;
    activity?: GroupActivityFields | null;
    discussionsCount?: number | null;
    groupPublicId?: string;
    groupSlug?: string;
    initiallyMember?: boolean;
    initiallyLeader?: boolean;
    viewerLeadershipLevel?: GroupLeadershipLevel;
    initiallyRequested?: boolean;
    initiallyBanned?: boolean;
    privateAccess?: boolean;
    sensitive?: boolean;
    leaders?: GroupLeaderIdentity[];
    orderingStyle?: GroupDiscussionOrderingStyle;
    onOrderingStyleChange?: (value: string) => void;
    orderingOptions?: GroupPageSelectOption[];
    showOrderingControl?: boolean;
    /** The Questions filter chip's state, and whether this section has one at all. */
    questionsOnly?: boolean;
    onQuestionsOnlyChange?: (next: boolean) => void;
    showQuestionsFilter?: boolean;
    /** Content for the new-joiner welcome dialog + the "About this group" overflow entry. */
    welcomeContent?: GroupWelcomeContent;
    /**
     * Collapse the mobile hero to identity + counts + the primary action (`GROUP-11`, wave 8
     * 2026-08-22). Read only by {@link GroupMobileHeader}; the desktop rail is unaffected, because
     * there the hero lives beside the list rather than above it.
     */
    collapsedOnMobile?: boolean;
    /**
     * Switches the page to a section. When supplied, the header's count chips become the entry points
     * to the tabs they describe; when absent they render as the inert badges they have always been.
     *
     * <p>Optional rather than required on purpose — `ComponentPage`'s static previews and the
     * denied-access render have no page to switch, and neither should have to invent a no-op handler
     * to keep rendering a header.
     */
    onSectionChange?: (value: string) => void;
    /**
     * The section the page is showing right now, so a count chip pointing at it can say "you are here"
     * instead of offering navigation it cannot perform (`GROUP-10`).
     *
     * <p>Optional, and its ABSENCE is the status quo: every chip renders exactly as it did before this
     * existed. That is what lets the two callers adopt it independently — `ComponentPage`'s static
     * previews have no current section to report, and `GroupRail` (which owns the desktop rail header)
     * has one and does not pass it down yet.
     */
    currentSection?: string;
    /**
     * `GROUP-19`: the real address of a section, from the page's own URL builder, so a count chip is
     * an anchor a member can middle-click, copy, long-press or crawl rather than a button.
     *
     * <p>Optional, and its absence is the status quo exactly: a caller that passes nothing renders
     * the same buttons as before. Gated by the caller alongside `onSectionChange`, so a denied-access
     * render (`ERRORS-23`) has neither and its chips fall through to the inert treatment.
     */
    buildSectionHref?: (value: string) => string | undefined;
    localizer: Localizer;
};

/**
 * The three resting treatments of a group-header pill, derived from ONE geometry string.
 *
 * <p>They are constants rather than three literals because the defect they close (`GROUP-12`) was
 * exactly that: the visibility badge in `GroupDesktopRailHeader` was a hand-inlined copy of the chip's
 * class string, so the two could not be told apart and nothing could keep them from drifting either.
 * Deriving all three from one base makes "same box, different boundary" a property of the code rather
 * than of whoever edits it next.
 *
 * <p><b>The tap-target pair `relative touch-hit-44` is NOT in these constants, and must not be moved
 * into them.</b> Both guards on that mechanism read the SOURCE TEXT of the opening `<button>` tag —
 * `scripts/check-tap-target-mechanisms.mjs` check B, and its Jest twin
 * `components/ui/tapTargets.contract.test.tsx` — so a class list hidden behind a constant reference is
 * a call site they cannot see. Measured while writing this: hoisting it dropped the guard's site count
 * under `src/` from 21 to 20 and the script still reported a clean corpus, which is the "scan quietly
 * stopped matching" failure that file's anti-vacuity floors exist to catch. It stays literal, in the
 * tag, where the checks can find it.
 */
const GROUP_HEADER_CHIP_BOX_CLASSES = "rounded-full border px-3 py-1 text-xs";
const GROUP_HEADER_CHIP_FOCUS_CLASSES = "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";
const GROUP_HEADER_INERT_CHIP_CLASSES = `${GROUP_HEADER_CHIP_BOX_CLASSES} border-transparent bg-muted text-neutral-text`;
const GROUP_HEADER_NAVIGATING_CHIP_CLASSES = `${GROUP_HEADER_CHIP_BOX_CLASSES} border-border bg-muted text-neutral-text standard-hover ${GROUP_HEADER_CHIP_FOCUS_CLASSES}`;
// No `standard-hover` here, and that is the point rather than an omission: the hover wash IS this
// surface's "I will respond to you" signal (and it carries `cursor: pointer` with it), so the chip
// pointing at the section you are already on must not paint it. `bg-accent` is a different token from
// `--hover-wash`, so a hovered sibling and the current chip stay distinguishable. Keyboard reach and
// the focus ring are deliberately kept: the control is still a real, focusable button, exactly as the
// active entry of a tab bar is.
const GROUP_HEADER_CURRENT_CHIP_CLASSES = `${GROUP_HEADER_CHIP_BOX_CLASSES} border-border bg-accent font-medium text-text-main ${GROUP_HEADER_CHIP_FOCUS_CLASSES}`;

/**
 * A header metadata chip ("5 posts"), optionally the entry point to the tab it describes.
 *
 * <p><b>The interactive and inert forms are the same BOX, to the pixel.</b> Same padding, radius,
 * border width, background and type scale — a chip that grows when it becomes tappable moves the whole
 * header, and header rhythm on this surface is exactly what the 2026-07-24 regression broke.
 *
 * <p><b>What differs is the boundary COLOUR, and that is `GROUP-12`.</b> Until 2026-08-19 the two forms
 * were the same string, so at rest — the state a reader is in before touching anything — a control that
 * navigates and a label that does not were the same pixels; everything the button added
 * (`standard-hover`, the focus ring) shows nothing until hover or focus, and on a touch device the
 * hover half never shows at all. So the boundary now carries the meaning: `border-border` if this chip
 * goes somewhere, `border-transparent` if it does not. That is the design language's own vocabulary
 * (rule 1, elevation is borders not shadows) and `components/ui/badge.tsx`'s, whose inert `secondary`
 * variant is `border-transparent` and whose bordered `outline` variant is the hoverable one. The border
 * box is kept rather than dropped, so nothing reflows by 2px.
 *
 * <p>The coarse-pointer 44px floor therefore comes from `relative touch-hit-44` — a transparent
 * centred `::after` that hit-tests as this element and changes no layout — and never from
 * `min-h-11`, which would take the mobile chip row from ~26px to 44px on every phone. The known cost
 * is recorded in the design's §3.7: on a coarse-pointer viewport wide enough to render the desktop
 * rail, the chips wrap and the 44px boxes overlap in a ~10px band between rows.
 *
 * <p>No new copy: the accessible name is the chip's own text, which already says what it counts.
 */
// `GROUP-19` — the group's sections are real, separately addressable, server-served URLs, so a
// member who wants the member list in a second tab must be able to have it, and intercepting that
// click is the half an `href` alone would not fix. The predicate itself was hoisted to
// `main/navigation/NavigationPolicy.ts` on 2026-09-07 (`FA-R2`) when the copy this file's own comment
// said to hoist on turned out to be the tenth: one of the twelve omitted `altKey`.

/**
 * Honest community size (design `2026-09-10-honest-community-size-design.md`): the header's weekly activity
 * line, or "New group", where the member count chip was. INERT, because it counts nothing a tab lists (the
 * members chip used to open the Members tab, which the tab bar still does). It carries
 * `data-group-activity`, the hook its server twin in `group.jte` shares, and renders only what the
 * server decided.
 */
function GroupHeaderActivityChip({ item, dataPlaywrightId }: { item: GroupActivityItem; dataPlaywrightId: string }) {
    return (
        <div className={GROUP_HEADER_INERT_CHIP_CLASSES} data-playwright-id={dataPlaywrightId} data-group-activity={item.key}>
            {item.label}
        </div>
    );
}

/**
 * SV-139 (work order `designs/01-now/2026-09-24-sv139-group-header-members-chip-work-order.md` D1): the
 * group page header shows no visitors chip. Filtered HERE, for the two header twins only, so every other
 * surface keeps `getGroupActivityItems`' full line; `group.jte`'s server twin drops it the same way.
 */
function getGroupHeaderActivityItems(localizer: Localizer, activity: GroupActivityFields | null | undefined): GroupActivityItem[] {
    return getGroupActivityItems(localizer, activity).filter((item) => item.key !== "visitors");
}

function GroupHeaderCountChip({
    label,
    targetSection,
    currentSection,
    onSectionChange,
    buildSectionHref,
    dataPlaywrightId,
}: {
    label?: string | null;
    targetSection?: string;
    currentSection?: string;
    onSectionChange?: (value: string) => void;
    /**
     * The real address of a section, from the page's own URL builder (`GROUP-19`). Optional so this
     * chip still renders — as a button, exactly as before — for a caller with no slug to build one
     * from. The in-page behaviour is identical either way; only the browser-owned affordances
     * (middle-click, open-in-new-tab, copy-link-address, long-press, a rendering crawler) depend on
     * the href existing in the DOM.
     */
    buildSectionHref?: (value: string) => string | undefined;
    dataPlaywrightId: string;
}) {
    if (!label) {
        return null;
    }

    if (!targetSection || !onSectionChange) {
        return <div className={GROUP_HEADER_INERT_CHIP_CLASSES} data-playwright-id={dataPlaywrightId}>{label}</div>;
    }

    const isCurrent = Boolean(currentSection) && currentSection === targetSection;
    const chipClassName = `${isCurrent ? GROUP_HEADER_CURRENT_CHIP_CLASSES : GROUP_HEADER_NAVIGATING_CHIP_CLASSES} relative touch-hit-44`;
    const href = buildSectionHref?.(targetSection);

    // Left wired even when this IS the current section, because the page's own state layer already
    // absorbs it: `useHistoryBackedState` treats an equal next state as a no-op, so no history entry
    // is written and nothing re-renders. Removing the handler would be a second mechanism doing the
    // same job, and the two would drift. (`GROUP-10`.) The href is emitted for the current chip too —
    // "copy the link to the section I am on" is a real thing to want.
    if (!href) {
        return (
            <button
                type="button"
                onClick={() => onSectionChange(targetSection)}
                aria-current={isCurrent ? "page" : undefined}
                data-playwright-id={dataPlaywrightId}
                className={chipClassName}
            >
                {label}
            </button>
        );
    }

    return (
        <a
            href={href}
            onClick={(event) => {
                if (isBrowserOwnedClick(event)) {
                    return;
                }
                event.preventDefault();
                onSectionChange(targetSection);
            }}
            aria-current={isCurrent ? "page" : undefined}
            data-playwright-id={dataPlaywrightId}
            className={chipClassName}
        >
            {label}
        </a>
    );
}

function localize(localizer: Localizer, key: LocalizationKey, ...args: string[]): string {
    return localizer(key, ...args);
}

/**
 * How much of the track must stay clear on each side of the selected tab.
 *
 * <p>Sized to the scroll controls (`size-7` = 28px) plus the row's `gap-3` (12px), rounded down: a tab
 * scrolled to be *exactly* flush with the scrollport edge sits directly against the chevron and reads
 * as clipped even though every pixel of it is painted. "Fully visible" and "legible" are not the same
 * requirement, and the gap between them is what this constant buys.
 */
const SELECTED_TAB_CLEARANCE_PX = 36;

/**
 * `GROUP-113` / round 39 `1d` (D18): whether a leader's photo must give way to the plain letter avatar.
 *
 * The Members card masks a sensitive person's avatar through `SensitiveMediaGuard`, but the header byline
 * and the Leaders chip draw it at 20 px, too small for the guard's overlay and reveal, and the chip is a
 * link (a reveal button inside it would nest interactive content). So these two spots show the initial,
 * with no overlay and no reveal; the reveal stays on the Members card. Same predicate as the guard: the
 * person is sensitive, the viewer's preference is not SHOW, and the viewer is not that person (the owner
 * exemption `UserCardDisplayer` applies). `sensitive` absent reads as not sensitive.
 */
function masksLeaderAvatar(leader?: GroupLeaderIdentity | null): boolean {
    if (!leader?.sensitive) return false;
    const username = leader.username?.trim();
    if (username && username === MetaHandler.getLoggedInUsername()?.trim()) return false;
    return shouldMaskSensitiveMedia(true, getSensitivePreferenceMode());
}

/** The creator's entry in the loaded leaders list, which is the only place their `sensitive` flag travels. */
function findLeader(leaders: GroupLeaderIdentity[] | undefined, username?: string | null): GroupLeaderIdentity | undefined {
    const wanted = username?.trim();
    if (!wanted || !Array.isArray(leaders)) return undefined;
    return leaders.find((leader) => leader?.username?.trim() === wanted);
}

function LeadershipPreviewChip({
    leader,
    localizer,
}: {
    leader: GroupLeaderIdentity;
    localizer: Localizer;
}) {
    const username = leader.username?.trim();
    const label = getDisplayUsername(username, localizer);
    const profilePath = getUserProfilePath(username);
    const content = (
        <>
            <ProfileImage localizer={localizer}
                imageUrl={masksLeaderAvatar(leader) ? undefined : leader.profileImageUrl}
                username={username ?? label}
                mode="user"
                hoverable={false}
                className="size-5"
            />
            <span translate={hasDisplayUsername(username) ? "no" : undefined} className="truncate">{label}</span>
        </>
    );

    return hasDisplayUsername(username) && profilePath ? (
        <a key={username} className="inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-text-main" href={profilePath}>
            {content}
        </a>
    ) : (
        <span key={username ?? label} className="inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-text-main">
            {content}
        </span>
    );
}

/**
 * Whether the private-group WALL owns this page's join actions, so the two headers must not draw a
 * second copy of them.
 *
 * `ERRORS-24` / wave-14 `Q10` ("a beginning: one panel"). The wall is now heading + sentence + the
 * request control + its expectation in ONE block, which was blocked until the operator said which of
 * the two live "Request to join" controls yields. The answer is the header's.
 *
 * **One predicate, three readers**, deliberately: `GroupPage`'s wall panel decides whether to render
 * the control by calling this, and both header components decide whether to withhold theirs by
 * calling the same function. So the two cannot disagree, and the failure direction of any future
 * drift is the STATUS QUO (`false` -> the headers keep their block and the panel renders none), never
 * two controls and never none anywhere.
 *
 * It mirrors `GroupController.viewGroup`'s denied branch — `privateAccess && !memberOrLeader`, with a
 * banned viewer taking `BannedUserCardDisplayer` instead of the wall — rather than reading the
 * server's own `deniedAccess` flag, because the desktop header is mounted by `GroupRail`, a file the
 * batch that landed this may not open (wave-15 charter, the scope fence). ⚠️ Retire this derivation
 * when someone may touch `GroupRail`: one optional `deniedAccess` prop forwarded through it replaces
 * the whole function with the server's answer.
 */
export function groupWallOwnsJoinActions({
    privateAccess,
    initiallyMember,
    initiallyLeader,
    initiallyBanned,
}: {
    privateAccess?: boolean;
    initiallyMember?: boolean;
    initiallyLeader?: boolean;
    initiallyBanned?: boolean;
}): boolean {
    return Boolean(privateAccess) && !initiallyMember && !initiallyLeader && !initiallyBanned;
}

function GroupVisibilityDescription({
    privateAccess,
    localizer,
    className,
}: {
    privateAccess?: boolean;
    localizer: Localizer;
    className?: string;
}) {
    return (
        <p className={className ?? "text-sm leading-5 text-muted-foreground"}>
            {privateAccess
                ? localize(localizer, "group.visibility.private.description")
                : localize(localizer, "group.visibility.public.description")}
        </p>
    );
}

/**
 * Post ordering for the Posts tab.
 *
 * Before P2-A this was `HeaderSelectControls` and carried a section `<select>` alongside the
 * ordering one, rendered from four places. Sections are now a tab bar, so all that is left is the
 * ordering control — hence the narrower name and the single rendered row.
 */
function OrderingSelectControl({
    orderingStyle,
    onOrderingStyleChange,
    orderingOptions = [],
    localizer,
    dataPlaywrightId,
}: {
    orderingStyle?: GroupDiscussionOrderingStyle;
    onOrderingStyleChange?: (value: string) => void;
    orderingOptions?: GroupPageSelectOption[];
    localizer: Localizer;
    dataPlaywrightId?: string;
}) {
    if (orderingOptions.length === 0) {
        return null;
    }

    return (
        <SimpleSelect
            value={orderingStyle}
            onValueChange={onOrderingStyleChange}
            options={orderingOptions}
            size="sm"
            className="w-auto min-w-[9.5rem] flex-none xl:min-w-[10.5rem]"
            ariaLabel={localizer("group.discussions.ordering.aria")}
            dataPlaywrightId={dataPlaywrightId}
        />
    );
}

/**
 * A queue size for a tab, as a pill after the label (`Join requests · 2`).
 *
 * <p>Mirrors `SectionCountBadge` in `DiscussionPageSectionControls` — the tab precedent this whole
 * row follows — rather than inventing a second badge shape for the same job.
 *
 * <p><b>Zero renders nothing.</b> Not a dimmed `0`, not an empty pill: the ABSENCE of a badge is what
 * a leader reads as "this queue is empty", and a `0` says the same thing in a way that still demands
 * to be looked at.
 *
 * <p>A capped variant (`99+`) existed for the board tab's unread count and went with it — no caller
 * produces a capped count any more, and an unproduced branch is an untested branch.
 */
function GroupTabCountBadge({ count, active, dataPlaywrightId }: { count?: number; active: boolean; dataPlaywrightId?: string }) {
    if (typeof count !== "number" || !Number.isFinite(count) || count <= 0) {
        return null;
    }
    return (
        <span
            aria-hidden="true"
            data-playwright-id={dataPlaywrightId}
            className={`rounded-full px-1.5 py-0.5 text-[0.6875rem] font-semibold leading-none tabular-nums ${
                active ? "bg-primary/10 text-foreground" : "bg-muted text-muted-foreground"
            }`}
        >
            {String(count)}
        </span>
    );
}

/**
 * Shared tab/pill row used by both the member tab bar and the manage console sub-navigation.
 *
 * Follows the in-repo tab precedent (`DiscussionPageSectionControls`): a horizontally scrollable
 * `role="tablist"` with an underline indicator, one hover wash, one focus ring, no shadow.
 */
function GroupTabList({
    items,
    activeValue,
    onSelect,
    buildItemHref,
    ariaLabel,
    dataPlaywrightId,
    tabIdPrefix,
    trailing,
    className,
    showScrollAffordance = false,
    localizer,
}: {
    localizer: Localizer;
    items: GroupPageSelectOption[];
    activeValue?: string;
    onSelect?: (value: string) => void;
    /**
     * The real address of an item, or `undefined` when it has none (`GROUP-19`).
     *
     * <p>Opt-in per caller, and that is the point: the member tab bar's three items ARE addressable
     * server-served routes (`/group/<slug>/{discussions,members,about}`), while the manage console's
     * tools that share this component are not. A caller that passes nothing renders exactly the
     * buttons this component always rendered.
     */
    buildItemHref?: (value: string) => string | undefined;
    ariaLabel: string;
    dataPlaywrightId: string;
    tabIdPrefix: string;
    trailing?: React.ReactNode;
    /** Outer spacing, so the owner of the rhythm is the always-rendered tab bar. */
    className?: string;
    /**
     * Render the left/right scroll controls when the track overflows.
     *
     * <p>Opt-in because the member tab bar carries four items that always fit, while the manage
     * console carries up to eight tools — five of which used to be reachable only by guessing that
     * an unscrollbarred strip scrolls. A hidden tool is an undiscoverable tool.
     */
    showScrollAffordance?: boolean;
}) {
    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const syncScrollAffordance = React.useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        setCanScrollLeft(track.scrollLeft > 1);
        setCanScrollRight(track.scrollLeft < maxScrollLeft - 1);
    }, []);

    /**
     * Bring the selected tab fully inside the track, with the scroll controls' width kept clear.
     *
     * <p>Scrolls the TRACK, never the element. `Element.scrollIntoView` walks every scrollable
     * ancestor, so on a deep link to a late tool it also scrolls the document — the page jumps to the
     * console strip before the reader has seen the page. Here the only thing that moves is
     * `track.scrollLeft`.
     *
     * <p><b>Idempotent by construction:</b> a tab already fully visible returns without writing
     * `scrollLeft`. That is what makes it safe to call from a resize observer and from the scroll
     * handler's own state changes without either feeding the other.
     *
     * <p>Measured off `getBoundingClientRect`, not `offsetLeft`: the track is `position: static`, so
     * `offsetParent` is some ancestor and `offsetLeft` is not measured from the scrollport.
     */
    const scrollActiveTabIntoView = React.useCallback((behavior: ScrollBehavior) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const activeTab = track.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
        if (!activeTab) {
            return;
        }
        const trackBox = track.getBoundingClientRect();
        const tabBox = activeTab.getBoundingClientRect();
        const clearedLeft = trackBox.left + SELECTED_TAB_CLEARANCE_PX - tabBox.left;
        const clearedRight = tabBox.right + SELECTED_TAB_CLEARANCE_PX - trackBox.right;

        let nextScrollLeft: number;
        if (clearedLeft > 0) {
            nextScrollLeft = track.scrollLeft - clearedLeft;
        } else if (clearedRight > 0) {
            nextScrollLeft = track.scrollLeft + clearedRight;
        } else {
            return;
        }
        const left = Math.max(0, nextScrollLeft);
        // `scrollTo` is not universally present on an Element — jsdom does not implement it at all, and
        // a throw here happens inside a passive effect during mount, which takes the whole console
        // header down rather than degrading. Assigning `scrollLeft` is the universally supported form
        // and differs only in that it cannot animate.
        if (typeof track.scrollTo === "function") {
            track.scrollTo({ left, behavior });
        } else {
            track.scrollLeft = left;
        }
    }, []);

    /*
     * The track's own width is the thing to watch, not the window's.
     *
     * The scroll controls are flex SIBLINGS of the track, rendered from `canScrollLeft`/`canScrollRight`
     * — so they mount one commit AFTER the first measurement and take ~40px off the track when they do.
     * A selected tab that was flush against the right edge is then underneath the chevron, which is
     * both how the active manage tool came to paint as "Post su" and how Formalize measured the
     * selected tool sitting at 357-428px in a track whose visible width ended at 348px. A window
     * `resize` listener cannot see any of it: the window never changed size.
     */
    React.useEffect(() => {
        const track = trackRef.current;
        if (!showScrollAffordance || !track) {
            return;
        }
        syncScrollAffordance();
        if (typeof ResizeObserver === "undefined") {
            const handleResize = () => syncScrollAffordance();
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }
        const observer = new ResizeObserver(() => {
            syncScrollAffordance();
            scrollActiveTabIntoView("auto");
        });
        observer.observe(track);
        return () => observer.disconnect();
    }, [items.length, showScrollAffordance, syncScrollAffordance, scrollActiveTabIntoView]);

    // Without this, a deep link to a tool late in the strip lands on a track scrolled to item 1 with
    // no selected tab in sight — the active underline is real but off-screen, so the surface reads as
    // though only the first few tools exist.
    React.useEffect(() => {
        if (!activeValue) {
            return;
        }
        scrollActiveTabIntoView(prefersReducedMotion() ? "auto" : "smooth");
        syncScrollAffordance();
    }, [activeValue, items.length, scrollActiveTabIntoView, syncScrollAffordance]);

    const scrollByStep = React.useCallback((direction: -1 | 1) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        track.scrollBy({
            left: direction * Math.max(120, Math.round(track.clientWidth * 0.7)),
            behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
    }, []);

    if (items.length === 0 && !trailing) {
        return null;
    }

    const scrollControlClasses = "relative touch-hit-44 inline-flex size-7 shrink-0 items-center justify-center self-center rounded-full border border-border bg-card text-muted-foreground transition-colors standard-hover focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

    return (
        <div className={`flex w-full min-w-0 items-stretch gap-3 border-b border-border${className ? ` ${className}` : ""}`} data-playwright-id={dataPlaywrightId}>
            {showScrollAffordance && canScrollLeft ? (
                <button
                    type="button"
                    className={scrollControlClasses}
                    onClick={() => scrollByStep(-1)}
                    aria-label={localizer("scrollable-tabs.scroll_left")}
                    data-playwright-id={`${dataPlaywrightId}-scroll-left`}
                >
                    <ChevronLeft className="size-4" aria-hidden="true" />
                </button>
            ) : null}
            <div
                ref={trackRef}
                role="tablist"
                aria-label={ariaLabel}
                className="no-scrollbar flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto"
                style={{ WebkitOverflowScrolling: "touch" }}
                onScroll={showScrollAffordance ? syncScrollAffordance : undefined}
            >
                {items.map((item) => {
                    const isActive = item.value === activeValue;
                    const Icon = item.icon;
                    const href = buildItemHref?.(item.value);
                    // `scroll-mx-9` mirrors SELECTED_TAB_CLEARANCE_PX for the scrolls this component
                    // does not perform: the browser's own `scrollIntoView` when a tab takes keyboard
                    // focus honours scroll-margin, so tabbing to the last tool lands it clear of the
                    // chevron too.
                    const tabClassName = `relative flex shrink-0 scroll-mx-9 items-center gap-1.5 whitespace-nowrap px-2 py-2 text-sm font-medium transition-colors sm:px-3 [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ${isActive ? "text-foreground" : "text-muted-foreground hover:bg-muted"}`;
                    /*
                     * `GROUP-19`: an item with a real address renders as an ANCHOR, so middle-click,
                     * open-in-new-tab, copy-link-address, long-press and a RENDERING crawler all
                     * work — none of which a `<button>` can offer, however well it navigates. A plain
                     * left click is still the page's: intercepted, and handed to `onSelect` so the
                     * in-page transition is unchanged.
                     *
                     * THE ARIA ROLES STAY, and that is a decision rather than an omission. The
                     * sibling fix on the discussion page (`DISCUSSION-14`,
                     * `DiscussionPageSectionControls.tsx`) DROPPED `role="tab"` for a `<nav>` of
                     * anchors, because a tablist with no `role="tabpanel"` announces a panel that
                     * does not exist — and the same is true here. But four Playwright specs read
                     * this strip through `[role=tablist]` / `[role=tab]` / `aria-selected`
                     * (`group-page-layout-invariants`, `group-sections-and-governance`,
                     * `group-member-layout-invariants`, `group-back-navigation`), so retiring the
                     * roles is a coordinated four-file change, not a side effect of adding an href.
                     * Everything `GROUP-19` is actually about keys on the href being in the DOM, not
                     * on the element's ARIA role, so the two halves are separable and this is the
                     * half that is provable today. The a11y half is recorded as owed in
                     * designs/07-archive/runs/2026-08-23-ux-critique-remediation-wave-9/batch-J-group-and-discussion.md §12.
                     */
                    const tabProps = {
                        role: "tab" as const,
                        "aria-selected": isActive,
                        tabIndex: isActive ? 0 : -1,
                        "data-playwright-id": `${tabIdPrefix}-${item.key}`,
                        /*
                         * The item's ROUTE, published next to its key because the two are not the
                         * same string and nothing in the DOM used to say so. `key` is a camelCase
                         * identifier and `value` is the URL tail, and they coincided for the first
                         * seven console tools — so `group-member-layout-invariants.spec.mjs` deep-
                         * linked to `manage/${key}` and got away with it until `inviteRequests` /
                         * `manage/invite-requests` arrived on 2026-09-02 and failed it four times.
                         *
                         * A spec cannot import these definitions (it is `.mjs`), so without this the
                         * only options are to re-derive the mapping in the test — a second copy that
                         * drifts — or to infer it, which is the thing that just broke. Published
                         * once, here, so any consumer reads the real address instead of guessing it.
                         */
                        "data-section-path": item.value,
                        className: tabClassName,
                    };
                    const tabChildren = (
                        <>
                            {/*
                              * Label-only below `sm`. The icon costs `size-4` + `gap-1.5` = ~22px per tab,
                              * and the strip's mobile budget has been spent twice: at 390px the three
                              * original tabs plus the leader pill overflowed (fixed by making that pill
                              * icon-only, reclaiming ~95px), and a fourth tab re-opened the same defect
                              * with no second pill left to shrink.
                              *
                              * Dropping the icons rather than the labels is the honest direction: the label
                              * carries the meaning and the icon is decoration, so this degrades nothing.
                              * The tab strip is back to three (Posts/Members/About) now that the board tab
                              * has gone, and the width that frees is what the `Open chat` pill spends —
                              * both pills are icon-only below `sm` for the same reason. Pinned by
                              * `group-page-layout-invariants.spec.mjs` -> "every group tab is fully inside
                              * the tab strip at 390px", which iterates whatever tabs exist rather than
                              * hardcoding a count, so a fourth tab fails here instead of clipping silently.
                              */}
                            {Icon ? <Icon className="hidden size-4 sm:block" aria-hidden="true" /> : null}
                            <span>{item.label}</span>
                            <GroupTabCountBadge count={item.badgeCount} active={isActive} dataPlaywrightId={`${tabIdPrefix}-${item.key}-badge`} />
                            {item.badgeSrLabel ? <span className="sr-only">{item.badgeSrLabel}</span> : null}
                            {/*
                              * `bottom-0`, not `-bottom-px`: the track is `overflow-x-auto`, which
                              * computes `overflow-y: auto` too, so anything drawn below the button's
                              * own box is clipped away by the scrollport. The indicator used to sit
                              * outside it (paired with `-mb-px` on the button) and therefore never
                              * rendered on ANY viewport — the active tab was carried by weight alone.
                              */}
                            <span
                                aria-hidden="true"
                                data-playwright-id={`${tabIdPrefix}-${item.key}-indicator`}
                                className={`pointer-events-none absolute inset-x-2 bottom-0 h-0.5 origin-center rounded-full bg-primary transition-transform duration-200 ease-out ${isActive ? "scale-x-100" : "scale-x-0"}`}
                            />
                        </>
                    );

                    if (!href) {
                        return (
                            <button key={item.key} type="button" onClick={() => onSelect?.(item.value)} {...tabProps}>
                                {tabChildren}
                            </button>
                        );
                    }

                    return (
                        <a
                            key={item.key}
                            href={href}
                            onClick={(event) => {
                                // A modified or middle click is the member asking the BROWSER for
                                // this URL. Leave it alone.
                                if (isBrowserOwnedClick(event)) {
                                    return;
                                }
                                event.preventDefault();
                                onSelect?.(item.value);
                            }}
                            {...tabProps}
                        >
                            {tabChildren}
                        </a>
                    );
                })}
            </div>

            {showScrollAffordance && canScrollRight ? (
                <button
                    type="button"
                    className={scrollControlClasses}
                    onClick={() => scrollByStep(1)}
                    aria-label={localizer("scrollable-tabs.scroll_right")}
                    data-playwright-id={`${dataPlaywrightId}-scroll-right`}
                >
                    <ChevronRight className="size-4" aria-hidden="true" />
                </button>
            ) : null}

            {/* `gap-2` because the slot now holds two pills on a leader's view (Open chat, Manage
                group); with one child it is inert, so the single-pill rendering is unchanged. */}
            {trailing ? <div className="flex shrink-0 items-center gap-2 pb-1 pl-1">{trailing}</div> : null}
        </div>
    );
}

/**
 * The single control that takes a member to the group's conversation in Messages.
 *
 * <p>Design: `designs/01-now/2026-08-13-group-chat-repositioning-design.md` §5.1 and §5.3. It replaces
 * the board tab, the `Board · N` pill and the per-page-load fetch behind that pill, all three.
 *
 * <p><b>It is a real anchor, and that is the mechanism rather than the markup.</b> A member expects
 * middle-click, "open in new tab" and a status-bar preview from something that leaves the page, and
 * the app's own document-level anchor handler (`EnhancedNavigation`) already routes a same-origin
 * href through the one navigation chokepoint that mints a page instance. So this control writes no
 * history at all — no `pushState`, and above all no entry built by spreading the current one, which
 * would inherit `entryKind: "in-page"` and a still-matching `pageInstanceId` and quietly break Back
 * (AGENTS.md rule 33). Nothing here needs a handler to get that right; not having one is what gets it
 * right.
 *
 * <p><b>Deliberately no unread dot yet.</b> Design §5.2 requires the dot to come from the chat unread
 * the header already polls, so the group page makes no extra request. The header's SSR bootstrap runs
 * in `SUMMARY` mode (`HeaderInboxService`), which seeds an empty `previewThreads` and a single global
 * boolean `hasUnreadGroupBoardActivity` — "some group of yours has unread", never which one. Drawing
 * that boolean beside THIS group's control would claim something the data does not say, on the page
 * of whichever group the member happens to be reading. No per-group signal is reachable without a
 * request, and a request here is the thing this slice removed, so the dot waits for the header
 * payload to carry a group-scoped field.
 */
function GroupOpenChatEntry({
    href,
    localizer,
}: {
    href: string;
    localizer: Localizer;
}) {
    return (
        <a
            href={href}
            data-playwright-id="group-open-chat"
            // Icon-only below `sm`, exactly like the manage pill beside it: the tab strip is primary
            // navigation and would otherwise absorb this pill's width as overflow, clipping the last
            // tab mid-glyph. `aria-label` carries the name while the text is hidden.
            aria-label={localizer("chat.group.open_chat")}
            // Same pill recipe as the manage entry: one border (no shadow), one hover wash, one focus
            // ring, semantic tokens throughout — flat-X, and identical to its sibling so the two read
            // as one row of controls rather than two designs.
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5 text-sm font-medium text-text-main transition-colors hover:bg-muted focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:px-3 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
        >
            <MessagesSquare className="size-4 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{localizer("chat.group.open_chat")}</span>
        </a>
    );
}

/**
 * The group page's one and only `<h1>`, and the twin of the `<h1>` `group/group.jte` server-renders
 * inside the island container.
 *
 * WHY THIS IS A SEPARATE COMPONENT rather than a heading inside one of the two headers, which is what
 * `GROUP-2` originally proposed. `GroupDesktopRailHeader` and `GroupMobileHeader` are CSS-hidden
 * TWINS, not alternatives: both are always in the DOM (`lg:hidden` on the mobile wrapper in
 * `GroupPage.tsx`, `hidden lg:block` on the rail cell in `PageWithRailLayout`). So a heading placed in
 * either one sits inside `display: none` at the other breakpoint, where `display: none` removes it
 * from the accessibility tree entirely — a desktop screen-reader user would still have no page
 * heading. Placing it in both instead puts two `<h1>`s in every rendered document.
 *
 * Rendering it once, outside both, is the only shape where the rendered result has exactly one `<h1>`
 * at every viewport AND that `<h1>` is in the accessibility tree at every viewport.
 *
 * It is visually hidden because the group's name is already painted, twice, by the two headers.
 * Duplicating it visibly would change the design; not emitting it at all is what rule 32 forbids —
 * `createRoot()` deletes the server's `<h1>` at mount, so before this the whole route was indexed and
 * announced with zero `<h1>` elements.
 *
 * Placement is load-bearing too: it is mounted at the TOP of the main column, so the group's identity
 * precedes its feed in DOM order. On desktop the visible name lives in the rail, which is the second
 * grid cell, i.e. after the entire feed.
 */
export function GroupPageHeading({ name }: { name?: string | null }) {
    const displayName = name?.trim();

    if (!displayName) {
        return null;
    }

    return (
        <h1 className="sr-only" data-playwright-id="group-page-heading" translate="no">
            {displayName}
        </h1>
    );
}

/**
 * The member tab bar (Posts / Members / About), plus `Open chat` and the single leader-only entry
 * into the manage console. One instance serves every viewport — the pre-P2-A page rendered section
 * controls up to three times per viewport and had a separate mobile-only Content/About toggle on top
 * of that.
 */
export function GroupTabBar({
    tabs,
    section,
    onSectionChange,
    buildSectionHref,
    openChatHref,
    showManageEntry = false,
    onOpenManage,
    pendingLeaderActionCount = 0,
    localizer,
}: {
    tabs: GroupPageSelectOption[];
    section?: string;
    onSectionChange?: (value: string) => void;
    /** `GROUP-19`: the real address of a section, from the page's own URL builder. */
    buildSectionHref?: (value: string) => string | undefined;
    /**
     * Where the group's conversation lives, or null when there is nothing to point at — an anonymous
     * visitor (who has no Messages), or a render with no group id. Null renders no control, never a
     * dead one.
     */
    openChatHref?: string | null;
    showManageEntry?: boolean;
    onOpenManage?: () => void;
    /**
     * How many items across the whole console are waiting on a LEADER'S decision — join requests plus
     * post submissions, never the invitation or ban totals. See `getGroupPendingLeaderActionCount`.
     * Zero renders no badge at all, so a leader with a clear console sees the pill exactly as before.
     */
    pendingLeaderActionCount?: number;
    localizer: Localizer;
}) {
    const manageTitle = localizer("group.manage.title");
    const hasPendingLeaderWork = pendingLeaderActionCount > 0;
    // The pill carries `aria-label` because its text is hidden below `sm`, and an aria-label REPLACES
    // the accessible name — an `sr-only` span inside it would never be announced. So the count has to
    // be composed into the label itself rather than added beside it.
    const manageAccessibleName = hasPendingLeaderWork
        ? `${manageTitle} ${localizer("group.manage.tab.awaiting", String(pendingLeaderActionCount))}`
        : manageTitle;
    const manageEntry = showManageEntry ? (
        <button
            type="button"
            onClick={onOpenManage}
            data-playwright-id="group-open-manage-console"
            // Icon-only below `sm`: the tab strip is primary navigation and would otherwise absorb
            // this pill's width as overflow, clipping the last tab mid-glyph. `aria-label` carries
            // the name when the text is hidden; the visible label still labels it on wider screens.
            aria-label={manageAccessibleName}
            // `min-w-11` alongside `min-h-11`: below `sm` the label is hidden, so the content is a
            // bare 16px icon and the height floor alone left the only entrance to the whole console
            // ~38px wide (and rendered it as a vertical oval rather than a circle).
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5 text-sm font-medium text-text-main transition-colors hover:bg-muted focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:px-3 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
        >
            <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{manageTitle}</span>
            {/* The badge is the whole point of the pill on a phone, where the label is hidden: it is
                the only thing on the group page that says the console has work in it. Rendered
                through the same `GroupTabCountBadge` as the tabs so it cannot drift into a second
                badge shape, and zero renders nothing so an empty console reads as empty. */}
            <GroupTabCountBadge
                count={hasPendingLeaderWork ? pendingLeaderActionCount : undefined}
                active={false}
                dataPlaywrightId="group-manage-pending-badge"
            />
        </button>
    ) : null;

    return (
        <GroupTabList
            items={tabs}
            activeValue={section}
            onSelect={onSectionChange}
            // The member tabs ARE routes; the manage console's tools (the other `GroupTabList`
            // caller) are not, so it passes nothing and keeps rendering buttons. `GROUP-19`.
            buildItemHref={buildSectionHref}
            ariaLabel={localizer("group.feed.section_aria")}
            dataPlaywrightId="group-tab-bar"
            tabIdPrefix="group-tab"
            localizer={localizer}
            // `Open chat` before `Manage group`: it is the one every member can use, and the manage
            // pill is leader-only, so ordering it first keeps the common control in the same place
            // whether or not the viewer is a leader.
            trailing={openChatHref || manageEntry ? (
                <>
                    {openChatHref ? <GroupOpenChatEntry href={openChatHref} localizer={localizer} /> : null}
                    {manageEntry}
                </>
            ) : null}
            // The tab-bar-to-content gap is owned here, on an always-rendered element, so Posts,
            // Members and About share one rhythm. It used to come from the ordering control, which
            // only renders on Posts, leaving the other tabs flush against this border.
            className="mb-4"
        />
    );
}

/**
 * Manage-console header: identity of the console, a way back to the group, and the tool sub-nav.
 * Kept separate from the member tab bar so a moderator is never in doubt about which surface they
 * are on — the console replaces the tab bar rather than nesting inside it.
 */
export function GroupManageConsoleHeader({
    tools,
    section,
    onSectionChange,
    onLeaveConsole,
    localizer,
}: {
    tools: GroupPageSelectOption[];
    section?: string;
    onSectionChange?: (value: string) => void;
    onLeaveConsole?: () => void;
    localizer: Localizer;
}) {
    return (
        <section className="mb-4 space-y-3" data-playwright-id="group-manage-console">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-base font-semibold text-text-main">{localizer("group.manage.title")}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">{localizer("group.manage.subtitle")}</p>
                </div>
                <button
                    type="button"
                    onClick={onLeaveConsole}
                    data-playwright-id="group-manage-back"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-text-main transition-colors hover:bg-muted focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [@media(pointer:coarse)]:min-h-11"
                >
                    <ArrowLeft className="size-4" aria-hidden="true" />
                    <span>{localizer("group.manage.back_to_group")}</span>
                </button>
            </div>

            <GroupTabList
                items={tools}
                activeValue={section}
                onSelect={onSectionChange}
                ariaLabel={localizer("group.manage.nav_aria")}
                dataPlaywrightId="group-manage-nav"
                tabIdPrefix="group-manage-tab"
                localizer={localizer}
                // An owner has eight tools and a phone fits three. Without the controls the other
                // five are undiscoverable: the strip clips mid-label with no scrollbar, no fade and
                // no arrow, so "Post submissions" reads as the end of the list.
                showScrollAffordance={true}
            />
        </section>
    );
}

/**
 * Ordering control for the Posts tab. One instance for every viewport — the desktop-only and
 * mobile-only variants existed because each also carried its own copy of the section select, which
 * the tab bar replaced.
 */
/**
 * The discussions section's control row: the Questions filter chip on the left, the ordering select on
 * the right.
 *
 * <p><b>The chip lives INSIDE this row on purpose, and adds no container of its own.</b> The row is
 * already floored at `min-h-9` and already owns its own `pb-3`, so the section's height is identical
 * whether the chip renders or not. That is the direct lesson of the 2026-07-24 group-page regression:
 * the gap under the tab bar used to come from a control that renders only on the discussions section,
 * so every other tab collapsed flush against the bar. Spacing between two blocks must be owned by a
 * container that renders in every state — the tab-bar-to-content gap is owned by `GroupTabList`, and
 * nothing here may take it back.
 *
 * <p>The early return checks BOTH controls, so neither can silently suppress the other if a future
 * section wants one without the other.
 */
export function GroupContentOrderingControls({
    orderingStyle,
    onOrderingStyleChange,
    orderingOptions = [],
    showOrderingControl = false,
    questionsOnly = false,
    onQuestionsOnlyChange,
    showQuestionsFilter = false,
    localizer,
}: Pick<
    BaseHeaderProps,
    | "orderingStyle"
    | "onOrderingStyleChange"
    | "orderingOptions"
    | "showOrderingControl"
    | "questionsOnly"
    | "onQuestionsOnlyChange"
    | "showQuestionsFilter"
    | "localizer"
>) {
    if (!showOrderingControl && !showQuestionsFilter) {
        return null;
    }

    return (
        <section className="pb-3" data-playwright-id="group-content-ordering-controls">
            <div className="flex min-h-9 items-center justify-between gap-2">
                {showQuestionsFilter && onQuestionsOnlyChange ? (
                    <QuestionsFilterChip
                        active={questionsOnly}
                        onToggle={onQuestionsOnlyChange}
                        localizer={localizer}
                        dataPlaywrightId="group-discussions-questions-filter"
                    />
                ) : (
                    // Holds the left edge so the ordering select stays put whether or not the chip is
                    // there; `justify-between` with a single child would otherwise pull it left.
                    <span aria-hidden="true" />
                )}
                {showOrderingControl ? (
                    <OrderingSelectControl
                        orderingStyle={orderingStyle}
                        onOrderingStyleChange={onOrderingStyleChange}
                        orderingOptions={orderingOptions}
                        localizer={localizer}
                        dataPlaywrightId="group-content-ordering-select"
                    />
                ) : null}
            </div>
        </section>
    );
}

export function GroupDesktopRailHeader({
    name,
    description,
    thumbnailUrl,
    bannerUrl,
    mainLocale,
    discussionSubmissionPolicy,
    creatorUsername,
    creatorProfileImageUrl,
    createdAt,
    activity,
    discussionsCount,
    groupPublicId,
    groupSlug,
    initiallyMember,
    initiallyLeader,
    viewerLeadershipLevel,
    initiallyRequested,
    initiallyBanned,
    privateAccess,
    sensitive = false,
    leaders,
    welcomeContent,
    onSectionChange,
    buildSectionHref,
    currentSection,
    localizer,
}: BaseHeaderProps) {
    const df = new DateFormatter(localizer);
    const createdLabel = createdAt ? df.timeSince(createdAt) : null;
    const hasCreatorUsername = hasDisplayUsername(creatorUsername);
    const displayCreatorUsername = getDisplayUsername(creatorUsername, localizer);
    const creatorProfilePath = getUserProfilePath(creatorUsername);
    const creatorAvatarMasked = masksLeaderAvatar(findLeader(leaders, creatorUsername));
    const displayDescription = description?.trim() || localizer("group.about.description_fallback");
    const leadership = getGroupLeadership(leaders, creatorUsername, creatorProfileImageUrl);
    const leadershipPreview = leadership.slice(0, 3);
    const privateVisibilityLabel = getFriendlyPrivateBadgeLabel(localizer, "badge.group.private", privateAccess);
    const activityItems = getGroupHeaderActivityItems(localizer, activity);
    const discussionsBadgeLabel = getFriendlyCountBadgeLabel(localizer, "badge.group.discussions", discussionsCount);
    // The server-decided member count replaces the leaders chip (SV-139 D2); the Leaders box below already
    // names them. Since round 38 D15 + D-C1 it is the ROSTER count, cast included, so it always equals the
    // Members tab it opens. Absent or zero means no chip (private outsider, zero).
    const membersBadgeLabel = getFriendlyCountBadgeLabel(localizer, "badge.group.members", readMemberTotal(activity));
    const statusBadgeLabel = privateAccess ? localizer("group.visibility.private") : localizer("group.visibility.public");
    const wallOwnsJoinActions = groupWallOwnsJoinActions({ privateAccess, initiallyMember, initiallyLeader, initiallyBanned });
    // `SV-134` — see the tile below. `D7` — the visibility sentence is withheld from anyone already
    // inside a private group; being told to "request to join" something you belong to is the defect.
    const viewerIsInsideGroup = Boolean(initiallyMember) || Boolean(initiallyLeader);
    const { openZoom: openThumbnailZoom, lightbox: thumbnailLightbox } = useThumbnailLightbox({ localizer, site: "group" });

    // `rounded-xl`, not `2xl`: the design language pins ONE card radius (`rounded-xl` ≈14px,
    // `designs/04-live/2026-06-11-frontend-design-language-current-implementation.md` §"Card radius").
    // The 2026-07-30 refine moved `GroupCard` onto it and left both header twins behind at 16px, so the
    // header sat 2px rounder than every card it stacks with.
    return (
        <section className="rounded-xl border border-border bg-card" data-playwright-id="group-desktop-rail-header">
            {/* `?.trim() ||`, not `??`: the page root emits `data-banner-url=""` for a group with no
                banner, and `"" ?? thumbnailUrl` is `""` — which made this documented fallback dead
                code and rendered the no-image gradient over the group's own picture. */}
            <ProfileBanner imageUrl={bannerUrl?.trim() || thumbnailUrl} className="min-h-24  overflow-hidden rounded-t-xl">
                {privateVisibilityLabel ? (
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3 text-[var(--text-inverse)]">
                        <div className="rounded-full bg-[var(--modal-overlay)] px-3 py-1 text-xs ring-1 ring-[var(--text-inverse)]/40 backdrop-blur">
                            {privateVisibilityLabel}
                        </div>
                    </div>
                ) : null}
            </ProfileBanner>

            <div className="space-y-4 p-4">
                <div className="flex items-start gap-3">
                    {/* `-mt-8` so the `border-4 border-card` ring actually punches the avatar out of
                        the banner it overlaps — with the avatar fully below the band the ring had
                        nothing to cut out of and just ate 8px of a 56px picture. */}
                    <div className="-mt-8" data-playwright-id="group-desktop-rail-thumbnail">
                        {/* `SV-134`. This is a TERMINAL site: the member is already on the group this
                            tile identifies, so there is nowhere to navigate to and the tile opens the
                            picture. It used to pass `interactive={true}` with NO handler — it looked
                            clickable and did nothing, which is the defect the rule exists to remove;
                            `role="terminal"` now carries the affordance AND withholds it when there is
                            no picture to open. */}
                        <ProfileImage localizer={localizer}
                            imageUrl={thumbnailUrl ?? undefined}
                            username={name ?? undefined}
                            mode="group"
                            role="terminal"
                            className={"size-14 border-4 border-card"}
                            hoverable={false}
                            onClick={() => openThumbnailZoom(thumbnailUrl, name)}
                        />
                    </div>

                    <div className="min-w-0 flex-1 pt-0.5">
                        {/* `line-clamp-2`, not `truncate`: the group's name is this page's identity and
                            must not be elided on its own page. `truncate` also forces
                            `white-space: nowrap`, which silently cancelled the `flex-wrap` beside it. */}
                        <div className="flex max-w-full flex-wrap items-center gap-2 text-lg font-semibold text-text-main">
                            <span className="line-clamp-2 wrap-anywhere" data-playwright-id="group-desktop-rail-name">{name}</span>
                            {sensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </div>

                        <AuthorAttribution localizer={localizer}
                            name={displayCreatorUsername}
                            href={hasCreatorUsername ? creatorProfilePath : null}
                            imageUrl={creatorAvatarMasked ? undefined : creatorProfileImageUrl}
                            avatarSeed={creatorUsername ?? displayCreatorUsername}
                            details={createdLabel ? <time dateTime={createdAt ?? undefined}>{createdLabel}</time> : null}
                            className="mt-1"
                        />
                    </div>
                </div>

                <p className="text-sm leading-6 text-neutral-text">{displayDescription}</p>

                {/* The counts are the entry points to the tabs they describe, and the activity chips before them
                    (which replaced the member count, honest community size 2026-09-10) are inert because
                    they count nothing a tab lists. "N members" (SV-139, 2026-09-24, which replaced the leaders
                    chip in this slot; the roster count, cast included, since round 38 D-C1) points at the Members tab; the
                    visitors chip is filtered out of this header.
                    The visibility badge beside them counts nothing and goes nowhere, so it stays a
                    plain div — a row where every chip but one is tappable is worse than a row where
                    the tappable ones are the ones carrying numbers.

                    That decision stands, and `GROUP-12` is its unfinished visual half: the badge was a
                    hand-inlined copy of the chip's class string, so "goes nowhere" was a fact about the
                    markup that the pixels never stated. It now takes the shared INERT treatment, which
                    is the same box with no painted boundary. `GROUP-10` is the same question from the
                    other side — a chip pointing at the section already open — and it is answered by
                    saying so (`aria-current`, no hover wash) rather than by making it inert, which is
                    the reading this comment rules out. */}
                <div className="flex flex-wrap gap-2">
                    {activityItems.map((item) => (
                        <GroupHeaderActivityChip key={item.key} item={item} dataPlaywrightId={`group-desktop-rail-activity-${item.key}`} />
                    ))}
                    <GroupHeaderCountChip
                        label={discussionsBadgeLabel}
                        targetSection="discussions"
                        currentSection={currentSection}
                        onSectionChange={onSectionChange}
                        buildSectionHref={buildSectionHref}
                        dataPlaywrightId="group-desktop-rail-chip-discussions"
                    />
                    <GroupHeaderCountChip
                        label={membersBadgeLabel}
                        targetSection="members"
                        currentSection={currentSection}
                        onSectionChange={onSectionChange}
                        buildSectionHref={buildSectionHref}
                        dataPlaywrightId="group-desktop-rail-chip-members"
                    />
                    <div className={GROUP_HEADER_INERT_CHIP_CLASSES} data-playwright-id="group-desktop-rail-visibility-badge">{statusBadgeLabel}</div>
                    {/* Demoted to `text-xs` and moved onto the badge row it belongs to. Rendered at
                        `text-sm text-muted-foreground` directly under the description it followed, it
                        read as a second paragraph the owner wrote about their own group — the same fact
                        stated twice within 40px, in two indistinguishable treatments.

                        `ERRORS-40`: on the wall it was stated a THIRD time. The private branch here
                        ("Request to join to view this group's posts and participate.") and the wall's
                        own `group.access_denied` ("This is a private group. Request to join to view
                        posts and participate.") are the same instruction in two wordings, both on
                        screen in every captured frame. One carrier per screen, and the row says the
                        wall's is the one the member is looking for — so this one yields on the same
                        predicate that moves the request control. The PUBLIC branch is untouched, and so
                        is `GROUP-18`'s membership gate on the mobile twin. */}
                    {wallOwnsJoinActions || viewerIsInsideGroup ? null : (
                        <GroupVisibilityDescription
                            privateAccess={privateAccess}
                            localizer={localizer}
                            className="w-full text-xs leading-5 text-muted-foreground"
                        />
                    )}
                </div>

                {leadershipPreview.length > 0 ? (
                    <div className="rounded-2xl border border-border bg-muted/70 px-3 py-3">
                        {/* `group.section.leaders` ("Leaders"), NOT `group.about.leadership_title`
                            ("Leadership"): `GROUP-25` caught the desktop About tab heading this rail
                            block LEADERSHIP while the main-column card ~320px away, and the "1 leader"
                            chip on the same screen, both said LEADER(S) — two nouns for one
                            one-person list, in one glance, at a translation cost of 25 catalogs.
                            The chip and the card are the majority, so the rail is the cheap side to
                            move, and the key it moves onto already exists in all 25 catalogs. */}
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {localizer("group.section.leaders")}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {leadershipPreview.map((leader) => (
                                <LeadershipPreviewChip
                                    key={leader.username ?? getDisplayUsername(leader.username, localizer)}
                                    leader={leader}
                                    localizer={localizer}
                                />
                            ))}
                            {leadership.length > leadershipPreview.length ? (
                                <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs text-neutral-text">
                                    +{leadership.length - leadershipPreview.length}
                                </span>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {/* `ERRORS-24` / `Q10`: on the wall this row is the DUPLICATE, and it is the one that
                    yields. The whole block goes, not just the primary action, so the `···` overflow
                    (Share, Report group) travels with it into the panel rather than being lost — the
                    page still offers exactly what it offered, in one place instead of two. */}
                {wallOwnsJoinActions ? null : (
                <div className="flex w-full justify-start">
                    <GroupButtons
                        alignStart={true}
                        groupPublicId={groupPublicId!}
                        groupSlug={groupSlug ?? ""}
                        initiallyMember={Boolean(initiallyMember)}
                        initiallyLeader={Boolean(initiallyLeader)}
                        viewerLeadershipLevel={viewerLeadershipLevel}
                        groupName={name ?? ""}
                        groupDescription={description ?? ""}
                        welcomeContent={welcomeContent}
                        groupThumbnailUrl={thumbnailUrl ?? ""}
                        mainLocale={mainLocale ?? undefined}
                        discussionSubmissionPolicy={discussionSubmissionPolicy ?? undefined}
                        privateAccess={Boolean(privateAccess)}
                        sensitive={Boolean(sensitive)}
                        initiallyRequestedToJoin={Boolean(initiallyRequested)}
                        initiallyBanned={Boolean(initiallyBanned)}
                        compact={true}
                        surface="group_page_header"
                        localizer={localizer}
                    />
                </div>
                )}
            </div>

            {/* `SV-134` — the viewer for the terminal tile above. `fixed`-positioned, so where it sits
                in the tree is irrelevant to where it paints; it lives inside the section so it
                unmounts with the header rather than outliving it. */}
            {thumbnailLightbox}
        </section>
    );
}

export function GroupMobileHeader({
    name,
    description,
    thumbnailUrl,
    bannerUrl,
    mainLocale,
    discussionSubmissionPolicy,
    creatorUsername,
    creatorProfileImageUrl,
    createdAt,
    activity,
    discussionsCount,
    groupPublicId,
    groupSlug,
    initiallyMember,
    initiallyLeader,
    viewerLeadershipLevel,
    initiallyRequested,
    initiallyBanned,
    privateAccess,
    sensitive = false,
    welcomeContent,
    leaders,
    onSectionChange,
    buildSectionHref,
    currentSection,
    collapsedOnMobile = false,
    localizer,
}: BaseHeaderProps) {
    const df = new DateFormatter(localizer);
    const createdLabel = createdAt ? df.timeSince(createdAt) : null;
    const hasCreatorUsername = hasDisplayUsername(creatorUsername);
    const displayCreatorUsername = getDisplayUsername(creatorUsername, localizer);
    const creatorProfilePath = getUserProfilePath(creatorUsername);
    const creatorAvatarMasked = masksLeaderAvatar(findLeader(leaders, creatorUsername));
    const privateVisibilityLabel = getFriendlyPrivateBadgeLabel(localizer, "badge.group.private", privateAccess);
    const activityItems = getGroupHeaderActivityItems(localizer, activity);
    const discussionsBadgeLabel = getFriendlyCountBadgeLabel(localizer, "badge.group.discussions", discussionsCount);
    const membersBadgeLabel = getFriendlyCountBadgeLabel(localizer, "badge.group.members", readMemberTotal(activity));
    const displayDescription = description?.trim();
    const wallOwnsJoinActions = groupWallOwnsJoinActions({ privateAccess, initiallyMember, initiallyLeader, initiallyBanned });
    // `SV-134` / `D7` — the mobile twin of the desktop rail's two decisions, same reasoning.
    const viewerIsInsideGroup = Boolean(initiallyMember) || Boolean(initiallyLeader);
    const { openZoom: openThumbnailZoom, lightbox: thumbnailLightbox } = useThumbnailLightbox({ localizer, site: "group" });
    return (
        <div className="lg:hidden rounded-xl border border-border bg-card">
            {/* `rounded-t-xl` matches the card that clips it — ProfileBanner's base `rounded-t-3xl`
                is a 24px page-header radius and left a card-coloured crescent in each top corner.
                Kept in lockstep with the desktop twin above, on the one card radius the design
                language defines. `?.trim() ||` because a group with no banner arrives as `""`. */}
            {/* `min-h-24`, in lockstep with the desktop twin: the phone must never spend MORE of a
                shorter screen on an empty band than the desktop does. It was `min-h-28` (112px against
                96px), which is the taller half of `GROUP-13`. */}
            {/* `GROUP-11` (wave 8, 2026-08-22): on a non-default section the hero collapses. Measured
                on the members deep link at 412x915: header bottom 63, hero 88 -> 470 (382px = 41.7% of
                the viewport), tabs 532, search 560-582, chips 612-634, FIRST MEMBER CARD AT 663 =
                72.4% down the screen. Someone who arrived on /members asked for the people and got
                660px of context before one of them.

                It is a COLLAPSE, not a removal: a first-time visitor still has to know where they
                landed, so identity (picture, name, who made it), the count chips that navigate, and
                the one primary action all stay. What goes is the decorative band, the pitch and the
                visibility sentence - each of which is either repeated on the About tab or addresses a
                prospective member who is not why this screen was opened.

                The pattern is not new here. The 2026-07-30 run applied exactly this to the leader
                console in this file, on the same argument (`inManageConsole` in GroupPage swaps the
                hero for an identity strip); that fix was scoped to the manage routes and the member
                tabs did not get it. */}
            {collapsedOnMobile ? null : (
                <ProfileBanner imageUrl={bannerUrl?.trim() || thumbnailUrl} className="rounded-t-xl min-h-24">
                    {privateVisibilityLabel ? (
                        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3 text-[var(--text-inverse)]">
                            <div className="rounded-full bg-[var(--modal-overlay)] px-3 py-1 text-xs ring-1 ring-[var(--text-inverse)]/40 backdrop-blur">
                                {privateVisibilityLabel}
                            </div>
                        </div>
                    ) : null}
                </ProfileBanner>
            )}

            <div className={collapsedOnMobile ? "space-y-3 p-3" : "space-y-3 p-4"}>
                {/* `items-start`, not `items-end`: bottom-aligning the row makes the thumbnail's
                    position depend on how tall the text column is, so the moment the group name wraps
                    to the two lines this header now allows, the 64px picture drops ~10px and reads as
                    a rendering fault. Top alignment holds the picture against the FIRST line of the
                    name whatever its length — and it matches the desktop twin above, which has always
                    been `items-start`. */}
                <div className="flex items-start gap-3">
                    {/* `-mt-8`, for the reason the desktop twin carries it: the `border-4 border-card`
                        ring below is drawn to punch the picture OUT of the banner it overlaps, and with
                        the avatar entirely under the band there was nothing to cut out of — the ring
                        just ate 8px of a 64px picture. Half an overlap on a 64px avatar, against the
                        desktop's 32-of-56: matching the desktop FRACTION would push the larger mobile
                        avatar into the private-visibility pill the banner pins to its bottom-left. */}
                    {/* `-mt-8` only when there IS a band to punch the picture out of. Collapsed there
                        is none, so the negative margin would just pull the avatar under the card's
                        own top edge, and the `border-4 border-card` ring would eat 8px of a 64px
                        picture with nothing to cut out of. */}
                    <div className={collapsedOnMobile ? "" : "-mt-8"} data-playwright-id="group-mobile-thumbnail">
                        {/* `SV-134` — the mobile twin of the desktop rail tile, same reasoning. */}
                        <ProfileImage localizer={localizer}
                            imageUrl={thumbnailUrl ?? undefined}
                            username={name ?? undefined}
                            mode="group"
                            role="terminal"
                            className={collapsedOnMobile ? "size-12" : "size-16 border-4 border-card"}
                            hoverable={false}
                            onClick={() => openThumbnailZoom(thumbnailUrl, name)}
                        />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                        {/* Two lines, not one clipped line. `truncate` on a `flex-wrap` container is
                            self-cancelling (it sets `white-space: nowrap`), so the wrap this markup
                            asked for could never happen and the group's own name was cut mid-token. */}
                        <div className="flex max-w-full flex-wrap items-center gap-2 text-lg font-semibold text-text-main">
                            <span className="line-clamp-2 wrap-anywhere" data-playwright-id="group-mobile-name">{name}</span>
                            {sensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </div>
                        <AuthorAttribution localizer={localizer}
                            name={displayCreatorUsername}
                            href={hasCreatorUsername ? creatorProfilePath : null}
                            imageUrl={creatorAvatarMasked ? undefined : creatorProfileImageUrl}
                            avatarSeed={creatorUsername ?? displayCreatorUsername}
                            details={createdLabel ? <time dateTime={createdAt ?? undefined}>{createdLabel}</time> : null}
                            className="mt-1 text-sm"
                        />
                    </div>
                </div>

                {/*
                  * A member has already read the pitch and acted on it, so on a phone the header must
                  * be identity + state + actions rather than the About tab reprinted above every
                  * visit: the description is clamped (About owns the full text) and the
                  * "anyone can view this group and join the conversation" line — which addresses a
                  * PROSPECTIVE member — is dropped once the viewer is one.
                  */}
                {displayDescription && !collapsedOnMobile ? (
                    <p className={`text-sm leading-5 text-neutral-text${initiallyMember ? " line-clamp-2" : ""}`} data-playwright-id="group-mobile-description">
                        {displayDescription}
                    </p>
                ) : null}
                {/* `GROUP-18`: the gate is MEMBERSHIP only. It used to read
                    `initiallyMember || collapsedOnMobile`, and `collapsedOnMobile` is a SECTION
                    predicate (`section !== "discussions"`), not a membership one — so the sentence
                    written for a prospective member was also silenced for the anonymous visitor
                    standing on the About tab, which is the one screen that exists to explain the
                    group. A phone visitor saw less than the same visitor on a desktop, and the
                    private branch of the identical fact is deliberately carried through the same
                    collapse eleven lines below. */}
                {/* `ERRORS-40`: and on the wall it yields entirely — see the desktop twin's note. The
                    wall's own sentence is the one carrier there; this row is its paraphrase. */}
                {viewerIsInsideGroup || wallOwnsJoinActions ? null : (
                    <GroupVisibilityDescription privateAccess={privateAccess} localizer={localizer} className="text-sm leading-5 text-neutral-text" />
                )}
                {/* The visibility fact does not disappear with the band that carried it - a private
                    group has to say so on every section, or the collapse has subtracted something the
                    page was telling a visitor. Same reasoning as the manage console's identity strip,
                    which keeps its private badge for exactly this reason. */}
                {collapsedOnMobile && privateVisibilityLabel ? (
                    <div className="flex">
                        <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-neutral-text">
                            {privateVisibilityLabel}
                        </span>
                    </div>
                ) : null}

                {/* Same destinations as the desktop twin. Measured at 390px the row is ~230px
                    against ~358px available, so it does not wrap and the chips' coarse-pointer hit
                    expanders never meet — see the design's §3.7. */}
                <div className="flex flex-wrap gap-2">
                    {activityItems.map((item) => (
                        <GroupHeaderActivityChip key={item.key} item={item} dataPlaywrightId={`group-mobile-activity-${item.key}`} />
                    ))}
                    <GroupHeaderCountChip
                        label={discussionsBadgeLabel}
                        targetSection="discussions"
                        currentSection={currentSection}
                        onSectionChange={onSectionChange}
                        buildSectionHref={buildSectionHref}
                        dataPlaywrightId="group-mobile-chip-discussions"
                    />
                    <GroupHeaderCountChip
                        label={membersBadgeLabel}
                        targetSection="members"
                        currentSection={currentSection}
                        onSectionChange={onSectionChange}
                        buildSectionHref={buildSectionHref}
                        dataPlaywrightId="group-mobile-chip-members"
                    />
                </div>

                {/* The mobile-only Content/About panel toggle was retired in P2-A: About is now a
                    real tab on every viewport, served by the one shared GroupTabBar.

                    `justify-start` + a stretched primary: this card's every other row is flush to the
                    content left edge, and the one action the surface exists to offer was a 68px pill
                    in the opposite corner with ~240px of empty row beside it. */}
                {/* `ERRORS-24` / `Q10`, the mobile twin: the wall panel owns the request control, so
                    this row yields whole — the `···` overflow travels with it rather than being lost. */}
                {wallOwnsJoinActions ? null : (
                <div className="flex justify-start">
                    <GroupButtons
                        alignStart={true}
                        stretchPrimary={true}
                        groupPublicId={groupPublicId!}
                        groupSlug={groupSlug ?? ""}
                        initiallyMember={Boolean(initiallyMember)}
                        initiallyLeader={Boolean(initiallyLeader)}
                        viewerLeadershipLevel={viewerLeadershipLevel}
                        groupName={name ?? ""}
                        groupDescription={description ?? ""}
                        welcomeContent={welcomeContent}
                        groupThumbnailUrl={thumbnailUrl ?? ""}
                        mainLocale={mainLocale ?? undefined}
                        discussionSubmissionPolicy={discussionSubmissionPolicy ?? undefined}
                        privateAccess={Boolean(privateAccess)}
                        sensitive={Boolean(sensitive)}
                        initiallyRequestedToJoin={Boolean(initiallyRequested)}
                        initiallyBanned={Boolean(initiallyBanned)}
                        compact={true}
                        surface="group_page_header"
                        localizer={localizer}
                    />
                </div>
                )}

            </div>

            {/* `SV-134` — the viewer for the terminal tile above. `fixed`-positioned, so where it sits
                in the tree is irrelevant to where it paints; it lives inside the header so it unmounts
                with it rather than outliving it. */}
            {thumbnailLightbox}
        </div>
    );
}
